import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import global from '@helpers/global';
import { Device, from, until } from '@helpers/media';
import { scaleFadeIn } from '@helpers/variants';
import { ButtonStyles } from '@stories/Elements/Forms/Buttons/Button.styles';
import { Typography } from '@stories/Elements/Typography/Typography.styles';
import Containers from '@stories/Settings/Containers/Containers.styles';
import { Grid } from '@stories/Settings/Grids/Grid.styles';
import { m } from 'framer-motion';
import styled, { css } from 'styled-components';

const Container = styled.section`
  background-color: ${brand.grey[95]};
  height: 100%;
  overflow-y: auto;
`;

const Inner = styled(Containers.Wide)`
  max-width: 1590px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 0 0;

  min-height: 100vh;
  position: relative;

  --headerHeight: 103px;
  --columnWidth: 140px;
  --columnGap: 16px;
  --gridWidth: max(calc(5 * var(--columnWidth) + 4 * var(--columnGap)), 100%);

  @media ${from(Device.DesktopSmall)} {
    --headerHeight: 70px;
    max-width: 1500px;
    margin: 0 auto;
  }

  @media ${from(Device.DesktopLarge)} {
    --headerHeight: 78px;
    padding: 24px 80px 0;
  }
`;

const Header = styled.header`
  position: sticky;
  left: 0;
  top: 0;
  z-index: 3;

  width: var(--gridWidth);
  height: var(--headerHeight);
  background-color: ${brand.grey[95]};
`;

const HeaderInner = styled.div`
  position: sticky;
  left: 0;
  width: calc(100vw - 17px);
  background-color: ${brand.grey[95]};

  @media ${from(Device.TabletLarge)} {
    width: 100%;
    position: static;
  }
`;

const TitleContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 30px;
  padding: 12px 24px;
`;

const BackContainer = styled.div`
  grid-column: 1 / span 4;

  @media ${until(Device.TabletLarge)} {
    grid-column: 1 / span 12;
  }
`;

const BackLink = styled.div`
  display: flex;
  align-items: center;
  ${Typography.PStyleSmall};
  color: ${brand.black};
  cursor: pointer;
`;

const BackLinkIcon = styled.div`
  margin-right: 12px;

  svg {
    width: 16px;
    height: 8px;
    transform: rotate(-180deg);
  }
`;

const Title = styled.div`
  ${Typography.H5Style};
  grid-column: 5 / span 4;
  text-align: center;

  @media ${until(Device.TabletLarge)} {
    grid-column: 1 / span 12;
  }
`;

const LoadingWrapper = styled.div`
  min-height: 45vh;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ProductsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 30px;
  padding-bottom: 30px;
  position: sticky;
  z-index: 2;
  top: var(--headerHeight);
  background-color: ${brand.grey[95]};

  @media ${until(Device.TabletLarge)} {
    display: flex;
    gap: 12px;
    overflow-x: auto;
    overflow-y: hidden;
  }
`;

const FakeProductColumn = styled.div`
  @media ${until(Device.TabletLarge)} {
    display: none;
  }
`;

const ProductContainer = styled.div`
  display: flex;
  gap: 20px;
  background-color: ${brand.white};
  padding: 15px;
  position: relative;
  border-radius: 3px;
  border: 1px solid ${brand.grey[90]};

  &[data-is-new='true'] {
    &:before {
      content: '';
      width: 100%;
      height: 8px;
      background-color: ${brand.red.base};
      border-radius: 3px 3px 0 0;
      position: absolute;
      top: 0;
      left: 0;
    }
  }

  @media ${from(Device.TabletLarge)} {
    max-width: 280px;
    margin: auto;
  }

  @media ${until(Device.TabletLarge)} {
    flex-direction: column;
    align-items: center;
    padding: 40px 12px 24px;
    min-width: 280px;
  }
`;

const NewTag = styled.div`
  background-color: ${brand.red.base};
  color: ${brand.white};
  padding: 10px;
  position: absolute;
  top: 0;
  right: 0;

  @media ${until(Device.TabletLarge)} {
    right: initial;
    left: 50%;
    transform: translateX(-50%);
  }
`;

const ImageContainer = styled.div`
  aspect-ratio: 106 / 106;
  max-width: 106px;

  @media ${until(Device.TabletLarge)} {
    max-width: initial;
    aspect-ratio: initial;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 12px;
  }
`;

const Image = styled.img`
  max-width: 106px;

  @media ${until(Device.TabletLarge)} {
    max-width: 100px;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  width: 100%;

  @media ${until(Device.TabletLarge)} {
    margin-top: auto;
    display: block;
  }
`;

const cardInfoWrapperVariants = {
  hidden: {
    opacity: 0,
    height: 0,
    transition: {
      duration: 0.3,
    },
  },
  visible: {
    opacity: 1,
    height: 'auto',
    transition: {
      duration: 0.3,
    },
  },
};

const Collapsible = styled(m.div).attrs(({ hide }: { hide: boolean }) => ({
  initial: 'visible',
  animate: hide ? 'hidden' : 'visible',
  variants: cardInfoWrapperVariants,
}))<{ hide: boolean }>``;

const ContentInner = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;
`;

const ContentSummary = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;
`;

const ModelCodeWrapper = styled.div`
  margin-top: auto;
`;

const ModelCode = styled.div`
  ${Typography.PStyleSmall};
  ${fonts.helvetica['73-bold-extended']};
  color: ${brand.grey[75]};
  margin-bottom: 12px;

  @media ${until(Device.TabletLarge)} {
    display: none;
  }
`;

const ProductName = styled.div`
  ${fonts.helvetica['93-black-extended']};
  margin-bottom: 12px;
  font-size: 14px;
  line-height: 16px;
  text-align: center;
  text-transform: uppercase;

  @media ${until(Device.TabletLarge)} {
    display: none;
  }
`;

const ProductRating = styled.div`
  display: flex;
  justify-content: center; ;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 10px;

  @media ${until(Device.TabletLarge)} {
    text-align: center;
  }
`;

const SpecificationsContainer = styled.div``;

const HeaderTitle = styled.span`
  ${Typography.H5Style};
  ${fonts.helvetica['97-black-condensed']};
  padding: 28px 16px;
  display: block;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 20px;
    background-color: ${brand.black};
    width: 16px;
    height: 8px;
    clip-path: polygon(50% 100%, 0 0, 100% 0);
    transition: clip-path 0.25s ease-in-out;
    display: block;
  }
`;

const HeaderRow = styled(m.div).attrs<{ isOpen: boolean }>(({ isOpen }) => ({
  initial: 'hidden',
  animate: isOpen ? 'visible' : 'hidden',
}))<{ isOpen: boolean }>`
  border-bottom: 2px solid ${brand.grey[80]};
  user-select: none;
  cursor: pointer;
  transition: background 0.25s ease-in-out;

  &:first-child {
    border-top: 2x solid ${brand.grey[80]};
  }

  ${({ isOpen }) =>
    isOpen
      ? css`
          background: ${brand.grey[20]};

          ${HeaderTitle} {
            color: ${brand.white};

            &:after {
              clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
              background: ${brand.white};
            }
          }
        `
      : null}
`;

const RowWrapper = styled(m.div).attrs(() => ({
  variants: {
    hidden: {
      height: '0px',
    },
    visible: {
      height: 'auto',
    },
  },
  transition: {
    bounce: 0,
  },
}))`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Row = styled.div<{ items: number }>`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 30px;
  padding: 28px 40px;
  border-top: 2px solid ${brand.grey[80]};
  cursor: text;
  user-select: text;

  @media ${until(Device.TabletLarge)} {
    padding: 22px 16px;
    display: grid;
    grid-template-columns: repeat(${({ items }) => items}, 280px);
    gap: 12px;
    grid-auto-flow: column;
    overflow-x: auto;

    -ms-overflow-style: none; /* Internet Explorer 10+ */
    scrollbar-width: none; /* Firefox */
    &::-webkit-scrollbar {
      display: none; /* Safari and Chrome */
    }
  }

  &:nth-child(odd) {
    background-color: ${brand.white};
  }

  &:nth-child(even) {
    background-color: ${brand.grey[95]};
  }
`;

const RowTitle = styled.span`
  text-transform: uppercase;
  ${fonts.helvetica['87-heavy-condensed']};
  font-size: 18px;

  @media ${until(Device.TabletLarge)} {
    grid-column: 1;
    text-align: left;
    position: sticky;
    left: 0;
  }
`;

const RowValue = styled.span`
  ${fonts.helvetica['55-roman']}
  text-align: center;
  @media ${until(Device.TabletLarge)} {
    grid-column: span 1;
    grid-row: 2;
  }
`;

const SelectWrapper = styled.div`
  border: 2px solid ${brand.grey[90]};
  border-radius: 5px;
`;

const Select = styled.select`
  width: 100%;
  border: none;
  outline: none;
  border-right: 10px solid transparent; // Counter the fact the dropdown arrow doesn't move with padding, which is dumb //
  text-align: center;
  padding: 10px;
  border-radius: 0;
  ${fonts.helvetica['57-condensed']}

  option {
    font: inherit;
  }

  &:disabled {
    appearance: none;
  }
`;

const StickyHeader = styled.div`
  position: sticky;
  top: calc(-1 * var(--padding));
  background-color: ${brand.grey[95]};
  z-index: 2;
  padding: 15px 0px;
  border-bottom: 2px solid ${brand.grey[25]};

  @media ${until(Device.TabletLarge)} {
    top: calc(-1 * var(--padding) + ${global.navHeightSmall}px);
  }
`;

const VisibilityCheckHelper = styled.div`
  position: absolute;
  top: 100vh;
  left: 0;
  width: var(--gridWidth);
  height: calc(100% - 100vh - 40px);
  pointer-events: none;
`;

const CompareWrapper = styled(m.div).attrs({
  initial: 'hidden',
  animate: 'visible',
  exit: 'hidden',
  variants: scaleFadeIn(1, 0.6),
})<{ 'data-mobile-nav-offset'?: number }>`
  position: fixed;
  width: 100%;
  height: 172px;
  top: ${(props) => props['data-mobile-nav-offset'] ?? 0}px;
  background-color: rgba(0, 0, 0, 80%);
  z-index: 1000;
  padding: 22px;
  transition: top 0.15s ease-in-out;

  @media ${until(Device.TabletLarge)} {
    height: 100px;
  }

  ${Grid.Default} {
    height: 100%;

    @media ${until(Device.TabletLarge)} {
      padding: 0;
    }
  }
`;

const CompareItems = styled.div`
  grid-column: 1 / span 10;
  display: flex;
  align-items: center;
  grid-gap: 30px;
  flex-direction: row-reverse;

  @media ${until(Device.TabletLarge)} {
    display: none;
  }
`;

const CompareItem = styled.div`
  display: flex;
  align-items: center;
  flex: 1 1 25%;
  height: 100%;
  border: 1px dashed ${brand.white};
  border-radius: 2px;
  padding: 12px;
  position: relative;
  max-width: 25%;
`;

const CompareItemInner = styled.div`
  display: flex;
  align-items: center;
  gap: ${fluid(15, 20)};
`;

const CompareItemImageContainer = styled.div`
  width: 84px;
  height: 84px;
  background-color: ${brand.white};
  border-radius: 3px;
  aspect-ratio: 1 / 1;
`;

const CompareItemImage = styled.img``;

const CompareItemTitleContainer = styled.div``;

const CompareModelCode = styled.div`
  font-size: ${fluid(12, 14)};
  ${fonts.helvetica['63-medium-extended']};
  color: ${brand.grey[90]};
  margin-bottom: 6px;
`;

const CompareProductName = styled.div`
  font-size: ${fluid(12, 14)};
  ${fonts.helvetica['73-bold-extended']};
  color: ${brand.white};
  text-transform: uppercase;

  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
`;

const CompareClose = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
    color: ${brand.white};
  }
`;

const CompareButtonContainer = styled.div`
  grid-column: 11 / span 2;
  display: flex;
  flex-direction: column;

  @media ${until(Device.TabletLarge)} {
    grid-column: 1 / span 12;
    flex-direction: row;
    gap: ${fluid(15, 30, Device.Mobile, Device.TabletLarge)};
    justify-content: space-between;
  }
`;

const CompareButtonText = styled.div`
  color: ${brand.white};
  display: flex;
  flex-direction: column;

  @media ${until(Device.TabletLarge)} {
    font-size: 12px;
  }
`;

const CompareClearAll = styled.div`
  ${fonts.helvetica['73-bold-extended']};
  color: ${brand.white};
  font-size: 14px;
  line-height: 22px;
  margin-top: auto;
  text-decoration: underline;
  cursor: pointer;

  @media ${until(Device.TabletLarge)} {
    font-size: 12px;
    line-height: 12px;
  }

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;

const CompareButton = styled.div`
  @media ${from(Device.TabletLarge)} {
    margin-top: auto;
  }

  @media ${until(Device.TabletLarge)} {
    ${ButtonStyles.ButtonElement} {
      font-size: 11px;
      width: max-content;
      max-width: 100%;
    }
  }
`;

const CompareOverlay = styled(m.div).attrs({
  initial: 'hidden',
  animate: 'visible',
  exit: 'hidden',
  variants: scaleFadeIn(1, 0.6),
})`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 1001;
`;

const ProductCompareStyles = {
  Container,
  Inner,
  TitleContainer,
  Title,
  BackContainer,
  BackLink,
  BackLinkIcon,
  LoadingWrapper,
  ProductsContainer,
  FakeProductColumn,
  ProductContainer,
  NewTag,
  ImageContainer,
  Image,
  ContentContainer,
  ContentInner,
  ModelCode,
  ProductName,
  ProductRating,
  ButtonContainer,
  SpecificationsContainer,
  HeaderTitle,
  HeaderRow,
  Row,
  RowTitle,
  RowValue,
  RowWrapper,
  ModelCodeWrapper,
  Select,
  SelectWrapper,
  StickyHeader,
  VisibilityCheckHelper,
  Header,
  HeaderInner,
  ContentSummary,
  Collapsible,
  CompareWrapper,
  CompareItems,
  CompareItem,
  CompareItemInner,
  CompareItemImageContainer,
  CompareItemImage,
  CompareItemTitleContainer,
  CompareModelCode,
  CompareProductName,
  CompareClose,
  CompareButtonContainer,
  CompareButtonText,
  CompareClearAll,
  CompareButton,
  CompareOverlay,
};

export default ProductCompareStyles;
